import { useState, useMemo } from 'react';
import { Copy } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Textarea } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { copyToClipboard } from '@/lib/utils';
import { toast } from 'sonner';

export function TextTransform() {
  const [text, setText] = useState(
    'apple\nbanana\n  apple\norange\nbanana \n\n  papaya\n\nKiwi\n\nbanana',
  );
  const [opts, setOpts] = useState({
    trim: true,
    dedupe: true,
    sort: false,
    sortDesc: false,
    removeEmpty: true,
    collapseSpaces: true,
    numberLines: false,
    reverse: false,
  });

  const output = useMemo(() => {
    let lines = text.split('\n');
    if (opts.trim) lines = lines.map((l) => l.trim());
    if (opts.collapseSpaces) lines = lines.map((l) => l.replace(/\s+/g, ' '));
    if (opts.removeEmpty) lines = lines.filter((l) => l.length > 0);
    if (opts.dedupe) {
      const seen = new Set<string>();
      lines = lines.filter((l) => {
        if (seen.has(l)) return false;
        seen.add(l);
        return true;
      });
    }
    if (opts.sort) {
      lines = [...lines].sort((a, b) => a.localeCompare(b));
      if (opts.sortDesc) lines.reverse();
    }
    if (opts.reverse) lines.reverse();
    if (opts.numberLines) {
      const w = String(lines.length).length;
      lines = lines.map((l, i) => `${String(i + 1).padStart(w, ' ')}  ${l}`);
    }
    return lines.join('\n');
  }, [text, opts]);

  const linesIn = text.split('\n').length;
  const linesOut = output.split('\n').length;

  return (
    <div>
      <ToolHeader
        toolId="text-transform"
        title="Whitespace · dedupe · sort"
        description="Clean text: trim whitespace, remove blank/duplicate lines, sort, number, or reverse."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="space-y-2.5 lg:order-2">
          <div className="text-xs font-medium text-fg-muted mb-1">Operations</div>
          <Toggle
            label="Trim each line"
            on={opts.trim}
            onChange={(v) => setOpts((p) => ({ ...p, trim: v }))}
          />
          <Toggle
            label="Collapse multiple spaces"
            on={opts.collapseSpaces}
            onChange={(v) => setOpts((p) => ({ ...p, collapseSpaces: v }))}
          />
          <Toggle
            label="Remove empty lines"
            on={opts.removeEmpty}
            onChange={(v) => setOpts((p) => ({ ...p, removeEmpty: v }))}
          />
          <Toggle
            label="Remove duplicates"
            on={opts.dedupe}
            onChange={(v) => setOpts((p) => ({ ...p, dedupe: v }))}
          />
          <div className="border-t border-border pt-3 mt-2 space-y-2.5">
            <Toggle label="Sort A→Z" on={opts.sort} onChange={(v) => setOpts((p) => ({ ...p, sort: v }))} />
            {opts.sort && (
              <Toggle
                label="Reverse (Z→A)"
                on={opts.sortDesc}
                onChange={(v) => setOpts((p) => ({ ...p, sortDesc: v }))}
              />
            )}
            <Toggle
              label="Reverse line order"
              on={opts.reverse}
              onChange={(v) => setOpts((p) => ({ ...p, reverse: v }))}
            />
            <Toggle
              label="Number lines"
              on={opts.numberLines}
              onChange={(v) => setOpts((p) => ({ ...p, numberLines: v }))}
            />
          </div>
        </Card>

        <div className="lg:col-span-2 lg:order-1 space-y-3">
          <Card className="!p-0">
            <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
              <span className="text-xs font-medium text-fg-muted">Input</span>
              <span className="text-2xs text-fg-subtle font-mono">{linesIn} lines</span>
            </div>
            <Textarea
              mono
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="!border-0 !rounded-t-none min-h-[180px] !bg-transparent focus-visible:!ring-0"
              placeholder="Paste lines…"
            />
          </Card>

          <Card className="!p-0">
            <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
              <span className="text-xs font-medium text-fg-muted">Output</span>
              <div className="flex items-center gap-2">
                <span className="text-2xs text-fg-subtle font-mono">{linesOut} lines</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={async () => {
                    const ok = await copyToClipboard(output);
                    if (ok) toast.success('Copied');
                  }}
                >
                  <Copy size={11} />
                </Button>
              </div>
            </div>
            <pre className="font-mono text-[13px] leading-relaxed p-4 overflow-auto min-h-[180px] max-h-[400px] whitespace-pre-wrap">
              <code>{output}</code>
            </pre>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Toggle({
  label,
  on,
  onChange,
}: {
  label: string;
  on: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between text-sm cursor-pointer">
      <span className="text-fg-muted">{label}</span>
      <Switch checked={on} onChange={onChange} />
    </label>
  );
}

export default TextTransform;
